export const EP_PROJECT_V2_FIELDS = [
    { key: 'projectName', label: 'Project name or code', kind: 'text' },
    { key: 'principalName', label: 'Principal name and title', kind: 'text' },
    { key: 'operationalLocations', label: 'Operational countries and cities', kind: 'textarea' },
    { key: 'startDate', label: 'Project start date', kind: 'date' },
    { key: 'endDate', label: 'Project end date', kind: 'date' },
    { key: 'venueCount', label: 'Number of venues or sites', kind: 'number' },
    { key: 'movementLegCount', label: 'Number of movement legs', kind: 'number' },
    { key: 'principalThreatLevel', label: 'Principal threat level', kind: 'select', options: ['Critical', 'High', 'Medium', 'Low'] },
    { key: 'classification', label: 'Project classification', kind: 'select', options: ['Confidential', 'Restricted', 'Internal'] },
    { key: 'detailLead', label: 'Detail lead name', kind: 'text' },
    { key: 'agentCount', label: 'Total agents', kind: 'number' },
    { key: 'vehicleFleet', label: 'Vehicle fleet', kind: 'textarea' },
    { key: 'armouredVehicles', label: 'Armoured vehicles and rating', kind: 'textarea' },
    { key: 'principalBloodType', label: 'Principal blood type', kind: 'select', options: ['Unknown', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'], required: false },
    { key: 'principalMedications', label: 'Principal medications', kind: 'textarea', required: false },
    { key: 'knownThreatsRecentIncidents', label: 'Known Threats & Recent Incidents', kind: 'textarea', required: false,
        placeholder: 'List any specific threat actors, activist groups, or recent security incidents targeting the Principal or the selected venues.' },
    { key: 'localContactsLiaisons', label: 'Local Contacts & Liaisons', kind: 'textarea', required: false,
        placeholder: 'Does the client have any pre-existing local security, police, or medical contacts at the destination? Leave blank if none.' },
    { key: 'specialTacticalRequirements', label: 'Special Tactical Requirements', kind: 'textarea', required: false,
        placeholder: 'Specify any requirements for safe rooms, low-profile movement, or specialized medical equipment (e.g., trauma kits, oxygen).' },
] as const;

export const EP_PROJECT_VENUE_TYPES = ['Hotel', 'Conference centre', 'Government', 'Private', 'Outdoor', 'Transport hub', 'Other'] as const;

export const EP_PROJECT_CLIENT_FACTS = ['detailLead', 'agentCount', 'vehicleFleet', 'armouredVehicles',
    'principalBloodType', 'principalMedications'] as const;
export type EpProjectItineraryVenue = { name: string; country: string; type: string; date: string; address?: string };
export type EpProjectIntake = Partial<Record<(typeof EP_PROJECT_V2_FIELDS)[number]['key'], string>> & { venues?: EpProjectItineraryVenue[] };
const text = (value: unknown, limit = 240) => String(value ?? '').trim().slice(0, limit);
const agentCountText = (value: unknown) => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';

export function epProjectAgentCount(value: unknown): string | null {
    const count = agentCountText(value);
    return /^[0-9]+$/.test(count) && Number.isSafeInteger(Number(count)) ? String(Number(count)) : null;
}

export function normalizeEpProjectIntake(source: Record<string, unknown>): EpProjectIntake {
    const intake: EpProjectIntake = {};
    for (const field of EP_PROJECT_V2_FIELDS)
        intake[field.key] = field.key === 'agentCount' ? epProjectAgentCount(source[field.key]) ?? agentCountText(source[field.key])
            : text(source[field.key], field.kind === 'textarea' ? 4000 : 240);
    for (const key of ['principalBloodType', 'principalMedications'] as const)
        intake[key] ||= 'Unknown';
    intake.venues = Array.isArray(source.venues) ? source.venues.slice(0, 24).map(venue => ({
        name: text(venue?.name, 180), country: text(venue?.country, 100), type: text(venue?.type, 100), date: text(venue?.date, 40),
        ...(Object.hasOwn(venue || {}, 'address') ? { address: text(venue.address, 500) } : {}),
    })) : [];
    return intake;
}

export function epProjectClientValues(intake: Record<string, unknown>, previous: Record<string, string> = {}) {
    const values = { ...previous };
    for (const field of EP_PROJECT_V2_FIELDS)
        if (Object.hasOwn(intake, field.key))
            values[field.key] = field.key === 'agentCount' ? epProjectAgentCount(intake[field.key]) ?? agentCountText(intake[field.key])
                : text(intake[field.key], field.kind === 'textarea' ? 4000 : 240);
    return values;
}

export function epProjectEditedDocuments(documents: Record<string, string> = {}, generated: Record<string, string> = {}) {
    return Object.fromEntries(Object.entries(documents).filter(([key, content]) => content.trim() && content !== generated[key]));
}

export function epProjectClientVenues(intake: Record<string, unknown>, previous: any[] = []) {
    if (Array.isArray(intake.venues) && intake.venues.length)
        return normalizeEpProjectIntake(intake).venues!.map(venue => {
            const existing = previous.find(item => item.name === venue.name && item.country === venue.country);
            return { name: venue.name, country: venue.country, type: venue.type, surveyDate: venue.date,
                address: venue.address ?? text(existing?.address, 500),
                ep11RequestId: existing?.ep11RequestId || '', status: existing?.status || 'not_started' };
        });
    const venues = previous.slice(0, Number(intake.venueCount));
    while (venues.length < Number(intake.venueCount) && venues.length < 24)
        venues.push({ name: `Site ${venues.length + 1}`, country: '', type: '', surveyDate: '', ep11RequestId: '', status: 'not_started' });
    return venues;
}

// The start and end dates form one client input group.
export const EP_PROJECT_V2_GROUPS = [
    ['Project name or code', 'projectName'],
    ['Principal name and title', 'principalName'],
    ['Operational countries and cities', 'operationalLocations'],
    ['Project dates', 'startDate', 'endDate'],
    ['Number of venues or sites', 'venueCount'],
    ['Number of movement legs', 'movementLegCount'],
    ['Principal threat level', 'principalThreatLevel'],
    ['Project classification', 'classification'],
    ['Team and vehicles', 'detailLead', 'agentCount', 'vehicleFleet', 'armouredVehicles'],
    ['Principal medical facts', 'principalBloodType', 'principalMedications'],
    ['Tactical context', 'knownThreatsRecentIncidents', 'localContactsLiaisons', 'specialTacticalRequirements'],
] as const;

export const EP_PROJECT_V2_QA = [
    'Client intake, itinerary, and project reference reviewed',
    'Principal threat assessment and source dates reviewed',
    'Country intelligence and travel brief links reviewed',
    'Every required EP-11 venue survey delivered and linked',
    'Route roles and venue limitations reviewed',
    'Team, communications, and medical findings reviewed',
    'All seven document drafts reviewed and corrected',
    'Classification, distribution, and invoice amount confirmed',
] as const;

export const EP_PROJECT_LIVE_QA = [
    'Client intake, itinerary, and project reference reviewed',
    'Fresh venue threat evidence, source dates, and limitations reviewed',
    'Public source medical contacts and trauma facility capabilities confirmed',
    'Primary and alternate casevac routes reviewed for every venue',
    'Team, communications, and tactical requirements reviewed',
    'All source gaps recorded and reviewed',
    'All seven document drafts reviewed and corrected',
    'Classification, distribution, and invoice amount confirmed',
] as const;

export function epProjectLiveProgress(values: Record<string, unknown>, venues: any[], documents: Record<string, string>,
    _reviewNotes: Record<string, string> = {}) {
    return [
        !missingEpProjectV2Intake({ ...values, venues: venues.map(venue => ({ name: venue.name, country: venue.country, type: venue.type,
            date: venue.surveyDate })) }).length && venues.length === Number(values.venueCount),
        true,
        true,
        true,
        ['operationsOrder', 'threatAssessment', 'intelAnnex', 'advanceSummary', 'teamBriefing', 'commsMedical', 'aar']
            .every(key => Boolean(documents[key])),
    ];
}

export function missingEpProjectV2Intake(values: Record<string, unknown>, requireDetails = false) {
    const missing: string[] = EP_PROJECT_V2_FIELDS.filter(field => !('required' in field && !field.required) &&
        (requireDetails || !EP_PROJECT_CLIENT_FACTS.includes(field.key as never)) && !text(values[field.key])).map(field => field.label);
    const venueCount = Number(values.venueCount);
    const movementLegCount = Number(values.movementLegCount);
    if (values.venueCount && (!Number.isInteger(venueCount) || venueCount < 1 || venueCount > 24))
        missing.push('Venues must number between 1 and 24');
    if (values.movementLegCount && (!Number.isInteger(movementLegCount) || movementLegCount < 0))
        missing.push('Movement legs must be zero or more');
    if (values.startDate && values.endDate && String(values.endDate) < String(values.startDate))
        missing.push('End date must follow start date');
    if (values.operationalLocations && String(values.operationalLocations).split('\n').some(line => {
        const [country, city] = line.split('|');
        return !country?.trim() || !city?.trim();
    }))
        missing.push('Every operational location needs a country and city');
    for (const key of ['principalThreatLevel', 'classification'] as const) {
        const field = EP_PROJECT_V2_FIELDS.find(item => item.key === key)!;
        if (values[key] && !('options' in field && field.options.includes(String(values[key]) as never)))
            missing.push(`${field.label} must use a listed option`);
    }
    if (values.agentCount !== undefined && text(values.agentCount) &&
        epProjectAgentCount(values.agentCount) === null)
        missing.push('Total agents must use digits only (zero or more)');
    if (requireDetails || Array.isArray(values.venues)) {
        const venues = normalizeEpProjectIntake(values).venues!;
        if (venues.length !== venueCount)
            missing.push('Provide one itinerary entry for every venue');
        for (let index = 0; index < venues.length; index++) {
            const venue = venues[index];
            if (!venue.name || !venue.country || !venue.type || !venue.date)
                missing.push(`Venue ${index + 1} needs a name, country, type, and date`);
            if (venue.date && (!/^\d{4}-\d{2}-\d{2}$/.test(venue.date) || !Number.isFinite(Date.parse(venue.date)) ||
                new Date(venue.date).toISOString().slice(0, 10) !== venue.date ||
                venue.date < String(values.startDate || '') || venue.date > String(values.endDate || '')))
                missing.push(`Venue ${index + 1} date must fall within the project dates`);
            if (venue.country && !operationalCountries(values.operationalLocations).includes(venue.country))
                missing.push(`Venue ${index + 1} country must be an operational country`);
        }
    }
    return missing;
}

export function operationalAreas(value: unknown) {
    return String(value || '').split('\n').map(item => item.trim()).filter(Boolean).slice(0, 40);
}

export function operationalCountries(value: unknown) {
    return [...new Set(operationalAreas(value).map(item => item.split('|')[0].trim()).filter(Boolean))];
}

export function epProjectV2Progress(values: Record<string, unknown>, links: Array<{ type: string; status: string }>,
    venues: Array<{ ep11RequestId: string; status: string }>, documents: Record<string, string>,
    _reviewNotes: Record<string, string> = {}) {
    const complete = (status: string) => ['delivered', 'invoiced', 'completed'].includes(status);
    return [
        !missingEpProjectV2Intake(values).length,
        true,
        links.some(link => ['S-05', 'S-06'].includes(link.type) && complete(link.status)),
        links.some(link => link.type === 'TRM-01' && complete(link.status)),
        venues.length === Number(values.venueCount) && new Set(venues.map(venue => venue.ep11RequestId)).size === venues.length &&
            venues.every(venue => !!venue.ep11RequestId && complete(venue.status)),
        true,
        true,
        ['operationsOrder', 'threatAssessment', 'intelAnnex', 'advanceSummary', 'teamBriefing', 'commsMedical', 'aar']
            .every(key => Boolean(documents[key])),
    ];
}
